import { Color } from '@opallabs/gem'
import { getNestedProperty } from '@opallabs/lambda'
import { ApprovalStatus } from '@opallabs/satellite/src/ApprovalItem/types'
import { AssignmentStatus } from '@opallabs/satellite/src/AssignmentHelpers/types'
import {
  Assignment,
  ContextCollection,
  MomentCollection,
  ResponseCollection,
  ResponseRelationship,
  StageCollection,
} from '../../../types'
import { NormalizedApproval } from './types'

type AssignmentToApprovalMapper = (arg: {
  assignments: Assignment[]
  assignmentsContextMap: ContextCollection
  moments: MomentCollection
  responses: ResponseCollection
  stages: StageCollection
}) => NormalizedApproval[]

export const normalizeAssignmentsToApprovals: AssignmentToApprovalMapper = ({
  assignments,
  assignmentsContextMap,
  moments,
  responses,
  stages,
}) =>
  assignments.map((assignment) => {
    const normalizedApproval: NormalizedApproval = {
      approvalName: '',
      approvalStatus: ApprovalStatus.incomplete,
      assignmentStatus: AssignmentStatus.Default,
      commentText: '',
      contextLiveDate: new Date(),
      contextName: '',
      entityType: 'assignment',
      assignmentType: 'approval',
      hardDueDate: false,
      // @todo - override iconColor with assignment.stage.workflow.context.story.color if value exists
      iconColor: Color.dark.Surface.N000,
      iconName: 'moment',
      id: assignment.id,
      checkpointableDate: new Date(assignment.created_at),
      dueDate: undefined,
    }
    const stageId = getNestedProperty<string>(
      assignment,
      'relationships',
      'stage',
      'id'
    )
    if (stageId !== undefined) {
      const stage = stages[stageId]
      if (stage !== undefined) {
        normalizedApproval.approvalName = stage.metadata?.title
          ? stage.metadata.title
          : 'Approval Phase'
        normalizedApproval.phaseName = stage.metadata?.title
          ? stage.metadata.title
          : 'Approval Phase'
        if (stage.due_date !== null) {
          // If the stage has a due date, set normalizedApproval.dueDate
          // this also means that hardDueDate should be set to true
          normalizedApproval.dueDate = new Date(stage.due_date)
          normalizedApproval.hardDueDate = true
        }
        if (stage.activated_at !== null) {
          normalizedApproval.checkpointableDate = new Date(stage.activated_at)
        }
      }

      const context = assignmentsContextMap[assignment.id]
      const momentId = getNestedProperty<string>(
        context,
        'relationships',
        'moment',
        'id'
      )
      if (momentId !== undefined) {
        normalizedApproval.momentId = momentId
        const moment = moments[momentId]
        if (moment !== undefined) {
          normalizedApproval.contextName = moment.title || ''

          // Only set dates if moment.scheduled_at has a valid value
          if (moment.scheduled_at) {
            normalizedApproval.contextLiveDate = new Date(moment.scheduled_at)
            if (normalizedApproval.dueDate === undefined) {
              normalizedApproval.dueDate = new Date(moment.scheduled_at)
            }
          }
        }
      }
    }

    const responseRelationship = getNestedProperty<
      ResponseRelationship | ResponseRelationship[]
    >(assignment, 'relationships', 'responses')
    if (responseRelationship !== undefined) {
      if (
        !(
          Array.isArray(responseRelationship) &&
          responseRelationship.length === 0
        )
      ) {
        let responseId: string
        if (Array.isArray(responseRelationship)) {
          responseId = responseRelationship[responseRelationship.length - 1].id
        } else {
          responseId = responseRelationship.id
        }
        const response = responses[responseId]
        normalizedApproval.approvalStatus =
          response.details.approved === 'approved'
            ? ApprovalStatus.complete
            : ApprovalStatus.rejected
        normalizedApproval.commentText = response.details.reasoning || ''
        if (normalizedApproval.approvalStatus === ApprovalStatus.complete) {
          normalizedApproval.completedAt = new Date(assignment.updated_at)
        }
      }
    }

    return normalizedApproval
  })
